import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Moon, Sun, Bell, Shield, Save, User, MapPin, Loader2, Globe, Server, Check, Key } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import toast from 'react-hot-toast';

const Settings = () => {
  const { isDark, toggleTheme, colors } = useTheme();
  const { userProfile } = useAuth();
  
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    newUserAlerts: true,
    newPropertyAlerts: true,
    reportAlerts: true,
    bookingAlerts: false,
    language: 'en',
    autoApproveVerifiedHosts: false,
    requirePropertyReview: true,
    maxImagesPerProperty: 10,
    minPropertyPrice: 100,
    nearbyDistanceLimit: 10,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const docRef = doc(db, 'adminConfig', 'appSettings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSettings(prev => ({
          ...prev,
          nearbyDistanceLimit: data.nearbyDistanceLimit || 10,
          maxImagesPerProperty: data.maxImagesPerProperty || 10,
          minPropertyPrice: data.minPropertyPrice || 100,
          requirePropertyReview: data.requirePropertyReview ?? true,
          autoApproveVerifiedHosts: data.autoApproveVerifiedHosts ?? false,
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setChanged(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'adminConfig', 'appSettings');
      await setDoc(docRef, {
        nearbyDistanceLimit: settings.nearbyDistanceLimit,
        maxImagesPerProperty: settings.maxImagesPerProperty,
        minPropertyPrice: settings.minPropertyPrice,
        requirePropertyReview: settings.requirePropertyReview,
        autoApproveVerifiedHosts: settings.autoApproveVerifiedHosts,
        updatedAt: new Date(),
      }, { merge: true });
      toast.success('System configuration updated successfully');
      setChanged(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const SettingSection = ({ title, icon: Icon, description, children, delay }) => (
    <div 
      className={`card-premium p-6 md:p-8 animate-fadeInScale opacity-0 stagger-${delay}`}
      style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start gap-4 mb-6 pb-6" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-inner bg-gradient-to-br from-indigo-500/10 to-blue-500/10 border" style={{ borderColor: `${colors.primary}20` }}>
          <Icon className="w-6 h-6" style={{ color: colors.primary }} />
        </div>
        <div>
           <h3 className="text-lg font-black tracking-tight" style={{ color: colors.text }}>{title}</h3>
           {description && <p className="text-[13px] mt-1 font-medium" style={{ color: colors.textSecondary }}>{description}</p>}
        </div>
      </div>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  );

  const ToggleSetting = ({ label, description, value, onChange }) => (
    <div className="flex items-center justify-between gap-6 p-4 rounded-xl transition-colors hover:bg-black/5" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)' }}>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[14px]" style={{ color: colors.text }}>{label}</p>
        {description && (
          <p className="text-[12px] font-medium leading-snug mt-0.5 opacity-80" style={{ color: colors.textSecondary }}>{description}</p>
        )}
      </div>
      <button
        onClick={() => onChange(!value)}
        className="relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{ 
          backgroundColor: value ? colors.success : colors.border,
          boxShadow: value ? `0 0 10px ${colors.success}50` : 'none',
          '--tw-ring-color': colors.success
        }}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)]">
         <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: colors.primary }} />
         <p className="text-[14px] font-bold uppercase tracking-widest text-opacity-80" style={{ color: colors.textSecondary }}>Retrieving System Configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-16">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sticky top-0 z-10 py-4 bg-opacity-90 backdrop-blur-md" style={{ backgroundColor: `${colors.background}E6` }}>
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ color: colors.text }}>
            Platform Settings
          </h1>
          <p className="mt-1 text-[13px] font-bold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
            Global Administration Parameters
          </p>
        </div>
        
        <div className="flex items-center gap-4">
           {changed && (
             <span className="text-[12px] font-black uppercase tracking-widest animate-pulse" style={{ color: colors.warning }}>Unsaved Changes</span>
           )}
           <button
             onClick={saveSettings}
             disabled={saving || (!changed && false)} // Remove disabled state based on changed for testing or keep it. Let's keep it allowing save.
             className={`flex items-center px-6 py-3 rounded-xl font-bold text-[13px] text-white transition-all shadow-md ${saving ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
             style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
           >
             {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
             {saving ? 'UPDATING...' : 'SAVE CONFIGURATION'}
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* Profile Card */}
        <div className="xl:col-span-2">
           <div className="card-premium p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 animate-fadeInScale stagger-1 relative overflow-hidden" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none"></div>
              
              <div className="w-24 h-24 rounded-3xl flex items-center justify-center flex-shrink-0 shadow-lg text-white relative z-10" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}>
                 <User className="w-10 h-10" />
                 <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md p-1.5" style={{ color: colors.success }}>
                    <Check className="w-full h-full" />
                 </div>
              </div>
              
              <div className="text-center md:text-left z-10 flex-1">
                 <h2 className="text-2xl font-black" style={{ color: colors.text }}>{userProfile?.displayName || 'Root Administrator'}</h2>
                 <p className="text-[15px] font-medium opacity-80" style={{ color: colors.textSecondary }}>{userProfile?.email || 'admin@manam.com'}</p>
                 <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
                    <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest bg-blue-500/10 text-blue-500 border border-blue-500/20 flex items-center gap-1.5">
                       <Shield className="w-3 h-3" /> Super Admin
                    </span>
                    <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest bg-green-500/10 text-green-500 border border-green-500/20 flex items-center gap-1.5">
                       <Server className="w-3 h-3" /> System Access Granted
                    </span>
                 </div>
              </div>
           </div>
        </div>

        <SettingSection title="Interface & Localization" delay="2" icon={Globe} description="Control how the dashboard is presented to authenticated staff.">
          <div className="flex items-center justify-between gap-6 p-4 rounded-xl border transition-colors hover:bg-black/5" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }}>
            <div>
              <p className="font-bold text-[14px]" style={{ color: colors.text }}>Visual Theme</p>
              <p className="text-[12px] font-medium leading-snug mt-0.5 opacity-80" style={{ color: colors.textSecondary }}>Force light or dark mode engine across the dashboard.</p>
            </div>
            <button
              onClick={toggleTheme}
              className="p-3 rounded-xl transition-all shadow-sm flex items-center justify-center border hover:scale-105"
              style={{ backgroundColor: colors.background, borderColor: colors.border }}
              title="Toggle Theme"
            >
              {isDark ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-indigo-600" />}
            </button>
          </div>

          <div className="flex items-center justify-between gap-6 p-4 rounded-xl border transition-colors hover:bg-black/5" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }}>
            <div>
              <p className="font-bold text-[14px]" style={{ color: colors.text }}>System Language</p>
              <p className="text-[12px] font-medium leading-snug mt-0.5 opacity-80" style={{ color: colors.textSecondary }}>Choose the primary language for admin interface labels.</p>
            </div>
            <select
              className="px-4 py-2.5 rounded-xl text-[13px] font-bold focus:outline-none cursor-pointer appearance-none shadow-sm text-center"
              style={{ backgroundColor: colors.surfaceHover, border: `1px solid ${colors.border}`, color: colors.text }}
              value={settings.language}
              onChange={(e) => handleSettingChange('language', e.target.value)}
            >
              <option value="en">English (US)</option>
              <option value="ar">العربية (AR)</option>
            </select>
          </div>
        </SettingSection>

        <SettingSection title="Alerts & Telemetry" delay="3" icon={Bell} description="Configure notification triggers for system events.">
          <ToggleSetting label="Critical Email Digests" description="Receive summary emails for severe system alerts." value={settings.emailNotifications} onChange={(v) => handleSettingChange('emailNotifications', v)} />
          <ToggleSetting label="New User Onboarding" description="Alert when a new host or guest completes registration." value={settings.newUserAlerts} onChange={(v) => handleSettingChange('newUserAlerts', v)} />
          <ToggleSetting label="Listing Submissions" description="Ping when a new property requires moderation review." value={settings.newPropertyAlerts} onChange={(v) => handleSettingChange('newPropertyAlerts', v)} />
          <ToggleSetting label="Moderation Reports" description="Flag when users report content or generate disputes." value={settings.reportAlerts} onChange={(v) => handleSettingChange('reportAlerts', v)} />
        </SettingSection>

        <SettingSection title="Geospatial Boundaries" delay="4" icon={MapPin} description="Proximity algorithms and location clustering rules.">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 p-4 rounded-xl border" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }}>
            <div>
              <p className="font-bold text-[14px]" style={{ color: colors.text }}>Nearby Search Radius</p>
              <p className="text-[12px] font-medium leading-snug mt-0.5 opacity-80" style={{ color: colors.textSecondary }}>Determines what constitutes "Nearby" in km.</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="1"
                max="100"
                className="w-24 px-4 py-2.5 rounded-xl text-[15px] font-black focus:outline-none text-center shadow-inner"
                style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                value={settings.nearbyDistanceLimit}
                onChange={(e) => handleSettingChange('nearbyDistanceLimit', parseFloat(e.target.value))}
              />
              <span className="text-[12px] font-black text-gray-500 uppercase tracking-widest">KM</span>
            </div>
          </div>
          
          <div className="p-4 rounded-xl flex gap-4 mt-2" style={{ backgroundColor: colors.infoGhost, border: `1px solid ${colors.info}30` }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-500/20 text-blue-500 flex-shrink-0">
               <MapPin className="w-4 h-4" />
            </div>
            <div>
               <p className="text-[12px] font-black uppercase tracking-widest text-blue-500 mb-1">Algorithm Context</p>
               <p className="text-[12px] font-medium leading-relaxed opacity-90" style={{ color: colors.text }}>
                  If zero properties exist within this radius from the user's geo-point, the mobile app will fallback to displaying "Suggested Top Properties" globally. Keep the radius small in dense cities and larger in rural areas.
               </p>
            </div>
          </div>
        </SettingSection>

        <SettingSection title="Listing Governance" delay="5" icon={Key} description="Rules engine for property creation and host limits.">
          <ToggleSetting label="Mandatory Moderation" description="All new listings must be manually approved by admin before going live." value={settings.requirePropertyReview} onChange={(v) => handleSettingChange('requirePropertyReview', v)} />
          <ToggleSetting label="Trust List Exemption" description="Skip manual review if the host has KYC verification." value={settings.autoApproveVerifiedHosts} onChange={(v) => handleSettingChange('autoApproveVerifiedHosts', v)} />
          
          <div className="flex items-center justify-between gap-6 p-4 mt-4 rounded-xl border" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }}>
            <div>
              <p className="font-bold text-[14px]" style={{ color: colors.text }}>Asset Upload Quota</p>
              <p className="text-[12px] font-medium leading-snug mt-0.5 opacity-80" style={{ color: colors.textSecondary }}>Max images allowed per listing to save CDN bandwidth.</p>
            </div>
            <input
              type="number"
              min="1"
              max="50"
              className="w-20 px-4 py-2 rounded-xl text-[14px] font-bold focus:outline-none text-center shadow-inner"
              style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
              value={settings.maxImagesPerProperty}
              onChange={(e) => handleSettingChange('maxImagesPerProperty', parseInt(e.target.value))}
            />
          </div>

          <div className="flex items-center justify-between gap-6 p-4 rounded-xl border" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }}>
            <div>
              <p className="font-bold text-[14px]" style={{ color: colors.text }}>Price Floor Threshold</p>
              <p className="text-[12px] font-medium leading-snug mt-0.5 opacity-80" style={{ color: colors.textSecondary }}>Minimum allowable listed price per booking interval.</p>
            </div>
            <div className="flex items-center gap-2">
               <span className="text-[12px] font-black text-gray-500 uppercase tracking-widest">SAR</span>
               <input
                 type="number"
                 min="1"
                 className="w-24 px-4 py-2 rounded-xl text-[14px] font-bold focus:outline-none text-center shadow-inner"
                 style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                 value={settings.minPropertyPrice}
                 onChange={(e) => handleSettingChange('minPropertyPrice', parseInt(e.target.value))}
               />
            </div>
          </div>
        </SettingSection>

      </div>

    </div>
  );
};

export default Settings;